import React, { useState } from 'react';
import { Dialog } from 'primereact/dialog';
import Tab from 'react-bootstrap/Tab';
import Tabs from 'react-bootstrap/Tabs';

/**
 * `properties.icon` guarda o nome sob o qual a imagem foi registrada no MapBox
 * (`polo_<arquivo>`), não uma URL. A tabela precisa do arquivo em si.
 */
const iconeUrl = (icon) =>
    icon ? `/img/polos_geradores/grupos/${String(icon).replace(/^polo_/, '')}.png` : null;

/** Endereço do polo vem prefixado pelo próprio nome — o legado corta igual. */
const enderecoLimpo = (titulo, endereco) =>
    endereco ? endereco.replace(`${titulo},`, '').trim() : '';

/**
 * "Polos Geradores na Área" — porte do ModalPolosGeradores do mapa Google, aberto pelo
 * botão POLOS do card de área.
 *
 * Modal próprio em vez do legado porque aquele expõe uma exportação XLS presa a uma
 * chave de cache no Redis, que o fluxo de polos do MapBox (recorte client-side) não tem.
 * O "Exportar" do rodapé manda as linhas exibidas para o endpoint próprio do MapBox
 * (api.polos-geradores-mapbox.export).
 *
 * @param {{show: boolean, onHide: () => void, area: {name?: string}|null,
 *          polos: Array<{properties: object}>, onExport?: () => Promise<void>}} props
 */
export default function PolosDaAreaModal({ show, onHide, area, polos, onExport }) {
    const [exportando, setExportando] = useState(false);
    const linhas = polos.map((p) => p.properties || {});

    const exportar = async () => {
        setExportando(true);
        try {
            await onExport();
        } finally {
            setExportando(false);
        }
    };

    // Agrupamento por subcategoria, como a aba "Grupos" do legado. Polo sem
    // subcategoria cai em "Não informado" em vez de sumir da contagem.
    const grupos = Object.entries(
        linhas.reduce((acc, p) => {
            const grupo = p.sub_category || 'Não informado';
            acc[grupo] = (acc[grupo] || 0) + 1;
            return acc;
        }, {})
    ).sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0], 'pt-BR'));

    return (
        <Dialog
            header={`Polos Geradores na Área${area?.name ? ` — ${area.name}` : ''}`}
            visible={show}
            onHide={onHide}
            modal
            style={{ width: '60rem', maxWidth: '95vw' }}
            footer={
                onExport && (
                    <button
                        type="button"
                        className="btn btn-success btn-sm"
                        onClick={exportar}
                        disabled={exportando || linhas.length === 0}
                    >
                        <i className={`fa ${exportando ? 'fa-spin fa-spinner' : 'fa-file-excel'} mr-2`}></i>
                        {exportando ? 'GERANDO…' : 'EXPORTAR'}
                    </button>
                )
            }
        >
            <Tabs defaultActiveKey="polos" id="polos-da-area-tabs" className="mb-3">
                <Tab eventKey="polos" title={`Polos Geradores (${linhas.length})`}>
                    <div style={{ maxHeight: '60vh', overflow: 'auto' }}>
                        <table className="table table-striped table-bordered table-sm">
                            <thead>
                                <tr>
                                    <th>&nbsp;</th>
                                    <th>Nome</th>
                                    <th>Categoria</th>
                                    <th>Sub-Categoria</th>
                                    <th>Endereço</th>
                                </tr>
                            </thead>
                            <tbody>
                                {linhas.map((p, i) => (
                                    <tr key={`${p.title}-${i}`}>
                                        <td>
                                            {iconeUrl(p.icon) && (
                                                <img src={iconeUrl(p.icon)} alt="" width={20} height={20} />
                                            )}
                                        </td>
                                        <td>
                                            {p.title}
                                            {p.in_shopping === true && (
                                                <span className="badge badge-info ml-2">Loja em Shopping</span>
                                            )}
                                        </td>
                                        <td>{p.main_category}</td>
                                        <td>{p.sub_category}</td>
                                        <td>{enderecoLimpo(p.title, p.address)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </Tab>

                <Tab eventKey="grupos" title={`Grupos (${grupos.length})`}>
                    <div style={{ maxHeight: '60vh', overflow: 'auto' }}>
                        <table className="table table-striped table-bordered table-sm">
                            <thead>
                                <tr>
                                    <th>Grupo</th>
                                    <th>Quantidade</th>
                                </tr>
                            </thead>
                            <tbody>
                                {grupos.map(([grupo, total]) => (
                                    <tr key={grupo}>
                                        <td>{grupo}</td>
                                        <td>{total}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </Tab>
            </Tabs>
        </Dialog>
    );
}
